import { type GridColDef } from '@mui/x-data-grid-premium';

import { type PaginatorType } from 'app/main/components/hooks/usePaginator';
import { useIsModuleDisabled, useSettings } from 'app/main/components';
import { ViewTableUtils } from 'app/main/components/page-layouts';
import { renderDescription, renderUpdatableValue } from '../components/OrdersLinesTableUtils';
import OrdersLinesTableActions from './OrdersLinesTableActions';

type useOrdersLinesTableColumnsType = (params: {
	order: any;
	isEditable: boolean;
	selectedLines: any[];
	paginatorProps: PaginatorType;
	updateRefresh: VoidFunction;
}) => GridColDef[];

const useOrdersLinesTableColumns: useOrdersLinesTableColumnsType = params => {
	const { order, isEditable, selectedLines, paginatorProps, updateRefresh } = params;
	const { sysSettings, t } = useSettings();
	const isModuleDisabled = useIsModuleDisabled();

	const columns: GridColDef[] = [
		{
			field: 'orli_description',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_DESCRIPTION'),
			minWidth: 400,
			flex: 1,
			renderCell: ({ value, row }) => renderDescription({ row, isEditable }),
			hideable: false
		},
		{
			field: 'orli_quantity',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_QUANTITY'),
			minWidth: 100,
			renderCell: ({ value }) => (value !== 0 ? value : undefined) || '--',
			hideable: false
		},
		{
			field: 'itum_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ITEM_UNITOFMEASURE'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'orli_base_price_final',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_BASE_PRICE'),
			minWidth: 125,
			renderCell: ({ value, row }) =>
				renderUpdatableValue({
					value,
					newValue: row.orli_new_base_price,
					renderValue: ViewTableUtils.renderCurrency,
					showNewValue: isEditable && !(row.orli_base_price_custom >= 0)
				})
		},
		{
			field: 'orli_discount_final',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_DISCOUNT'),
			minWidth: 125,
			renderCell: ({ value, row }) =>
				renderUpdatableValue({
					value,
					newValue: row.orli_new_discount,
					renderValue: ViewTableUtils.renderPercentage,
					showNewValue: isEditable && !(row.orli_discount_custom >= 0)
				})
		},
		{
			field: 'orli_unit_price',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_UNIT_PRICE'),
			minWidth: 125,
			renderCell: ({ value, row }) =>
				renderUpdatableValue({
					value,
					newValue: row.orli_new_unit_price,
					renderValue: ViewTableUtils.renderCurrency,
					showNewValue: isEditable
				})
		},
		{
			field: 'orli_tot_value_tax_excl',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_TOT_TAX_EXCL'),
			minWidth: 125,
			renderCell: ({ value, row }) =>
				renderUpdatableValue({
					value,
					newValue: row.orli_new_tot_value_tax_excl,
					renderValue: ViewTableUtils.renderCurrency,
					showNewValue: isEditable
				}),
			hideable: false
		}
	];

	if (sysSettings.orders_lines_status_enabled === 'true') {
		columns.unshift({
			field: 'orst_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_ORDER_STATUS'),
			minWidth: 125,
			renderCell: ({ value, row }) => ViewTableUtils.renderSquareChip({ value, backgroundColor: row.orst_color })
		});
	}

	if (sysSettings.orders_expenses_manager_enabled === 'true') {
		columns.push({
			field: 'orli_unit_expenses_final',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_UNIT_EXPENSES'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderCurrency
		});
		columns.push({
			field: 'orli_tot_expenses',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_TOT_EXPENSES'),
			minWidth: 125,
			renderCell: ViewTableUtils.renderCurrency,
			hideable: false
		});
	}

	if (sysSettings.orders_lines_projects_enabled === 'true' && !isModuleDisabled('projects')) {
		columns.push({
			field: 'proj_title',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_PROJECT'),
			minWidth: 240,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'projects', uid: row.orli_proj_uid } })
		});
	}

	// columns.push({
	// 	field: 'orli_order',
	// 	type: 'number',
	// 	headerName: t('DETAILS_ORDERS_LINES_FIELD_ORDER'),
	// 	minWidth: 100
	// 	// renderCell: renderCellWithTooltip
	// });

	if (isEditable && selectedLines.length === 0) {
		columns.push({
			field: 'actions',
			type: 'actions',
			width: 120,
			getActions: rowProps => OrdersLinesTableActions({ rowProps, order, paginatorProps, updateRefresh }),
			hideable: false
		});
	}

	return columns;
};

export default useOrdersLinesTableColumns;
